import Payment from 'payment';
import axios from 'axios';
import { object, string } from 'yup';
import {
  normalizeResponseError,
  AxiosAuthResponse,
  AuthResponseError,
} from '../../../lib';

const { VITE_QB_PAYMENTS_BASE_URL } = import.meta.env;

interface CardFormValues {
  name: string;
  isDefault?: boolean;
  number: string;
  zip?: string;
  expiry: string;
  cvc: string;
}

interface BankAccountFormValues {
  name: string;
  accountNumber: string;
  phone: string;
  accountType: string;
  routingNumber: string;
  isDefault?: boolean;
}

interface CardPayload {
  name: string;
  cvc: string;
  default?: boolean;
  number: string;
  expMonth?: string;
  expYear?: string;
  address?: {
    postalCode: string;
  };
}

interface CardPayloadWrapper {
  card: CardPayload;
}

interface BankAccountPayload {
  bankAccount: {
    name: string;
    accountNumber: string;
    phone: string;
    accountType: string;
    routingNumber: string;
    default?: boolean;
  };
}

type FormValues = CardFormValues | BankAccountFormValues;

// Code from https://codesandbox.io/s/ovvwzkzry9?file=/App.js:3386-3403

function clearNumber(value = ''): string {
  return value.replace(/\D+/g, '');
}

export function formatCreditCardNumber(value?: string): string {
  if (!value) {
    return value || '';
  }

  const issuer = Payment.fns.cardType(value);
  const clearValue = clearNumber(value);
  let nextValue: string;

  switch (issuer) {
    case 'amex':
      nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
        4,
        10,
      )} ${clearValue.slice(10, 15)}`;
      break;
    case 'dinersclub':
      nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
        4,
        10,
      )} ${clearValue.slice(10, 14)}`;
      break;
    default:
      nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
        4,
        8,
      )} ${clearValue.slice(8, 12)} ${clearValue.slice(12, 19)}`;
      break;
  }

  return nextValue.trim();
}

interface AllValues {
  number?: string;
  [key: string]: any;
}

export function formatCVC(value?: string, allValues: AllValues = {}): string {
  const clearValue = clearNumber(value || '');
  let maxLength = 4;

  if (allValues.number) {
    const issuer = Payment.fns.cardType(allValues.number);
    maxLength = issuer === 'amex' ? 4 : 3;
  }

  return clearValue.slice(0, maxLength);
}

export function formatExpirationDate(value?: string): string {
  const clearValue = clearNumber(value || '');

  if (clearValue.length >= 3) {
    return `${clearValue.slice(0, 2)}/${clearValue.slice(2, 6)}`;
  }

  return clearValue;
}

export function formatFormData(data: Record<string, any>): string[] {
  return Object.keys(data).map((d) => `${d}: ${data[d]}`);
}

interface QBResponse {
  value?: any;
  [key: string]: any;
}

interface QBPaymentTokenResult {
  qbData?: QBResponse;
  errorMessage: string;
}

interface QBErrorWithErrors extends AuthResponseError {
  errors?: Array<{ message?: string }>;
}

export async function getQBPaymentToken(
  mode: string,
  values: FormValues,
): Promise<QBPaymentTokenResult> {
  const payload: CardPayloadWrapper | BankAccountPayload =
    mode === modes.CREDIT_CARD
      ? generateCardPayload(values as CardFormValues)
      : generateBankAccountPayload(values as BankAccountFormValues);

  const response: AxiosAuthResponse = await axios
    .post(`${VITE_QB_PAYMENTS_BASE_URL}/payments/tokens`, payload, {
      headers: { 'Content-Type': 'application/json' },
    })
    .catch((err) => {
      return normalizeResponseError('POST', '', {}, false)(err);
    });

  const { data: qbData, error: qbError } = response;
  let errorMessage = '';
  if (qbError || !qbData?.value) {
    errorMessage = 'Error calling qb';
    const qbErrorWithErrors = qbError as QBErrorWithErrors;
    if (Array.isArray(qbErrorWithErrors?.errors)) {
      errorMessage = qbErrorWithErrors.errors[0]?.message ?? errorMessage;
    }
  }
  return { qbData, errorMessage };
}

function generateCardPayload(state: CardFormValues): CardPayloadWrapper {
  const { name, isDefault, number, zip, expiry, cvc } = state;

  const expiryValues = expiry?.split('/');

  const card: CardPayload = {
    name,
    cvc,
    default: isDefault,
    number: number?.replace(/\s/g, ''),
  };

  if (Array.isArray(expiryValues)) {
    card.expMonth = expiryValues[0];
    card.expYear = expiryValues[1];
  }

  if (zip) {
    card.address = { postalCode: zip };
  }

  return {
    card,
  };
}

function generateBankAccountPayload(
  state: BankAccountFormValues,
): BankAccountPayload {
  const { name, accountNumber, phone, accountType, routingNumber, isDefault } =
    state;

  return {
    bankAccount: {
      name,
      accountNumber,
      phone,
      accountType,
      routingNumber,
      default: isDefault,
    },
  };
}

interface CCDisplayProps {
  lastFour?: string;
  cardTypeDisplay?: string | null;
}
export function getCCDisplay({
  lastFour,
  cardTypeDisplay = null,
}: CCDisplayProps): string {
  const lastFourDisplay = lastFour ? lastFour?.slice(-8) : '';
  return `${cardTypeDisplay || ''}****${lastFourDisplay}`;
}

export const cardSchema = object().shape({
  name: string().nullable().required('Required'),
  number: string().nullable().required('Required'),
  expiry: string().nullable().required('Required'),
  cvc: string().nullable().required('Required'),
});

export const bankAccountSchema = object().shape({
  name: string().nullable().required('Required'),
  accountNumber: string().nullable().required('Required'),
  phone: string().nullable().required('Required'),
  accountType: string().nullable().required('Required'),
  routingNumber: string().nullable().required('Required'),
});

export const modes = {
  CREDIT_CARD: 'CreditCard',
  BANK_ACCOUNT: 'BankAccount',
};

export type PaymentMode = (typeof modes)[keyof typeof modes];

export const paymentMethodTypes = {
  CHECK: 'Check',
  CREDIT_CARD: 'CreditCard',
  ACH: 'ACH',
  WIRE: 'Wire',
  OTHER: 'Other',
};
